import { useNostr } from '@/hooks/useNostr';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createNip17GiftWraps, decryptGiftWrap, createNip04Message, decryptNip04Message, type UnwrappedDM } from '@/lib/nip17';
import { useToast } from '@/hooks/useToast';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * 1. Fetch, Cache and Resolve DM Preferred Relays
 * Resolves priority: kind 10050 -> kind 10002 read-relays -> none
 */
export function useDMRelays(recipientPubkey: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['dmInboxes', recipientPubkey],
    queryFn: async (): Promise<{ relays: string[]; source: '10050' | 'nip65' | 'none' }> => {
      if (!nostr || !recipientPubkey) return { relays: [], source: 'none' };

      try {
        // Step A: Target kind 10050 DM relay lists
        const inboxes = await nostr.query([{ kinds: [10050], authors: [recipientPubkey], limit: 1 }]);
        if (inboxes[0]) {
          const urls = inboxes[0].tags.filter(t => t[0] === 'relay').map(t => t[1]);
          if (urls.length > 0) return { relays: urls, source: '10050' };
        }

        // Step B: Fallback to kind 10002 NIP-65 Read/Inbox relays
        const nip65s = await nostr.query([{ kinds: [10002], authors: [recipientPubkey], limit: 1 }]);
        if (nip65s[0]) {
          const urls = nip65s[0].tags
            .filter(t => t[0] === 'r' && (!t[2] || t[2] === 'read'))
            .map(t => t[1]);
          if (urls.length > 0) return { relays: urls, source: 'nip65' };
        }
      } catch (err) {
        console.error('Error resolving DM relays for:', recipientPubkey, err);
      }

      return { relays: [], source: 'none' };
    },
    staleTime: 3600000, // 1 hour caching
    enabled: !!nostr && !!recipientPubkey,
  });
}

/**
 * 2. Send Message via priority NIP-17 OR fallback to legacy NIP-04 (Damus model)
 */
export function useSendMessage(recipientPubkey: string) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: routeInfo } = useDMRelays(recipientPubkey);

  return useMutation({
    mutationFn: async ({ content, replyId }: { content: string; replyId?: string }) => {
      if (!nostr || !user) throw new Error('Sign-in required to make private chats.');

      const useNip17 = routeInfo && routeInfo.source !== 'none';

      if (useNip17) {
        // NIP-17 Private wrapped DM flow
        const { recipientWrap, senderWrap } = await createNip17GiftWraps({
          senderSigner: user.signer,
          senderPubkey: user.pubkey,
          recipientPubkey,
          content,
          replyToEventId: replyId
        });

        // Publish to targeted inbox relays + our own inbox relays
        await nostr.event(recipientWrap);
        await nostr.event(senderWrap);

        return { success: true, encryption: 'nip17' as const };
      } else {
        // LEGACY NIP-04 Fallback
        const legacyEventTemplate = await createNip04Message({
          senderSigner: user.signer,
          recipientPubkey,
          content
        });

        const legacyEvent = await user.signer.signEvent({
          kind: 4,
          content: legacyEventTemplate.content || '',
          tags: legacyEventTemplate.tags || [],
          created_at: legacyEventTemplate.created_at || Math.floor(Date.now() / 1000)
        });

        await nostr.event(legacyEvent);

        return { success: true, encryption: 'nip04' as const };
      }
    },
    onSuccess: (data) => {
      if (data.encryption === 'nip04') {
        toast({
          title: 'Sent with fallback ⚠️',
          description: 'Secure fallback NIP-04 DM initiated (Recipient missing kind-10050 inboxes).'
        });
      } else {
        toast({ title: 'Message Sent 🔒', description: 'NIP-17 Gift-wrapped chat rumor delivered.' });
      }
      queryClient.invalidateQueries({ queryKey: ['direct-messages-stream'] });
      queryClient.invalidateQueries({ queryKey: ['conversations-thread', recipientPubkey] });
    }
  });
}

/**
 * 3. Feed query to fetch and unwrap ALL incoming direct messages (both NIP-17 and kind 4 legacy dms)
 */
export function useDirectMessagesAll() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery({
    queryKey: ['direct-messages-stream', user?.pubkey],
    queryFn: async (): Promise<UnwrappedDM[]> => {
      if (!nostr || !user) return [];

      // Query inbound NIP-17 Gift-wraps (kind 1059) addressed to user
      const inboundWraps = await nostr.query([{ kinds: [1059], '#p': [user.pubkey], limit: 80 }]);

      // Query legacy NIP-04 events where either user was author OR recipient
      const inboundLegacy = await nostr.query([
        { kinds: [4], '#p': [user.pubkey], limit: 40 },
        { kinds: [4], authors: [user.pubkey], limit: 40 }
      ]);

      const unwrapped: UnwrappedDM[] = [];

      // Unwrap NIP-17s
      for (const wrap of inboundWraps) {
        const d = await decryptGiftWrap(user.signer, wrap);
        if (d) unwrapped.push(d);
      }

      // Decrypt NIP-04s
      for (const legacy of inboundLegacy) {
        const isMeAuthor = legacy.pubkey === user.pubkey;
        const peerPubkey = isMeAuthor 
          ? (legacy.tags.find(t => t[0] === 'p')?.[1] || '') 
          : legacy.pubkey;

        if (!peerPubkey) continue;

        const d = await decryptNip04Message(user.signer, legacy, peerPubkey);
        if (d) unwrapped.push(d);
      }

      // Dedup by message contents or ID
      const seen = new Set<string>();
      return unwrapped.filter(msg => {
        const uniqueKey = msg.id + '_' + msg.content;
        if (seen.has(uniqueKey)) return false;
        seen.add(uniqueKey);
        return true;
      }).sort((a, b) => b.created_at - a.created_at);
    },
    enabled: !!nostr && !!user,
  });
}

/**
 * 4. Filters DM streams into distinct conversation counterpart lists
 */
export function useDirectMessages() {
  const { data: messages = [], isLoading } = useDirectMessagesAll();
  const { user } = useCurrentUser();

  const conversationPeers = (() => {
    if (!user) return [];

    const peers: Record<string, { lastMessage: UnwrappedDM; unread: boolean }> = {};

    messages.forEach(msg => {
      const peer = msg.pubkey === user.pubkey 
        ? (msg.tags.find(t => t[0] === 'p')?.[1] || '') 
        : msg.pubkey;

      if (!peer || peer === user.pubkey) return;

      if (!peers[peer]) {
        peers[peer] = {
          lastMessage: msg,
          unread: false
        };
      }
    });

    return Object.entries(peers).map(([peerPubkey, details]) => ({
      peerPubkey,
      ...details
    }));
  })();

  return { data: conversationPeers, isLoading };
}

/**
 * 5. Returns thread for a singular conversation
 */
export function useChatMessages(peerPubkey: string) {
  const { data: messages = [], isLoading } = useDirectMessagesAll();
  const { user } = useCurrentUser();

  const thread = (() => {
    if (!user || !peerPubkey) return [];

    return messages.filter(msg => {
      const isMySent = msg.pubkey === user.pubkey && msg.tags.some(t => t[0] === 'p' && t[1] === peerPubkey);
      const isTheirSent = msg.pubkey === peerPubkey;
      return isMySent || isTheirSent;
    }).sort((a, b) => a.created_at - b.created_at);
  })();

  return { data: thread, isLoading };
}

/**
 * 6. Managing your OWN kind 10050 inbox configurations settings page helper
 */
export function usePublishDMRelayList() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (relays: string[]) => {
      if (!nostr || !user) throw new Error('Must sign-in to publish configurations lists.');

      const tags = relays.map(r => ['relay', r]);

      const event = await user.signer.signEvent({
        kind: 10050,
        content: '',
        tags,
        created_at: Math.floor(Date.now() / 1000)
      });

      await nostr.event(event);
      return event;
    },
    onSuccess: () => {
      toast({ title: 'Configured Inbox 📬', description: 'Kind-10050 DM relay preferences broadcasted!' });
    }
  });
}
